/**
 *  Keys for feature flags. There's a matching .cs file.
 */
export default class FeatureFlags {

    /**
     * Enables calendar planner in application UI
     */
    public static readonly Calendar = "CALENDAR" as const;

    /**
     * Enables work order requests creation page
     */
    public static readonly WorkOrderCreationRequests = "WORK_ORDER_CREATION_REQUESTS" as const;

    /**
     * Controls Admin CRUD view of extra charge types, and adding extra charges on a work order
     */
    public static readonly ExtraCharge = "EXTRA_CHARGE" as const;

    /**
     * Enables Sync service
     */
    public static readonly Sync = "SYNC" as const;

    /**
     * Enables displaying and modification of users employee number in UserManagement page.
     */
    public static readonly EmployeeNumber = "EMPLOYEE_NUMBER" as const;

    /**
     * Enables option to add rental equipment to work order.
     */
    public static readonly RentalEquipment = "RENTAL_EQUIPMENT" as const;

    /**
     * Enables new full-text-search functionality for construction site page.
     */
    public static readonly ConstructionSiteFullTextSearch = "CS_FULLTEXT_SEARCH" as const;

    /**
     * Enables MySqlMatchSearchMode.Boolean for {@see ConstructionSiteFullTextSearch}
     */
    public static readonly ConstructionSiteFullTextSearchBooleanMode = "CS_FULLTEXT_SEARCH_BOOLEAN_MODE" as const;

    /**
     * Enables detailed search functionality for construction site page.
     */
    public static readonly ConstructionSiteDetailedSearch = "CS_DETAILED_SEARCH" as const;

    /**
     * Enables and displays alarm job functions on the UI.
     */
    public static readonly AlarmJobs = "ALARM_JOBS" as const;

    /**
     * Enables and displays comment field for user salary hours on the UI.
     */
    public static readonly UserSalaryHoursComment = "HOURS_COMMENT" as const;

    /**
     * Displays rental mass products on the catalog and on work order pages.
     */
    public static readonly RentalMassProducts = "RENTAL_MASS_PRODUCTS" as const;

    /**
     * If enabled when a cost pool is selected from a dropdown in the related mounter dropdown all mounters on pages that use Work Order Panel
     * who have that cost pool set will be automatically selected.
     */
    public static readonly CostPoolMounterAutoSelectInWorkOrdersPage = "COST_POOL_MOUNTER_AUTO_SELECT_IN_WORK_ORDERS_PAGE" as const;


    /**
     * If enabled when a cost pool is selected from a dropdown in the related mounter dropdown all mounters on pages that don't use Work Order Panel
     * who have that cost pool set will be automatically selected.
     */
    public static readonly CostPoolMounterAutoSelect = "COST_POOL_MOUNTER_AUTO_SELECT" as const;

    /**
     * Enables sending work order notifications to external personnel
     */
    public static readonly ExternalNotifications = "EXTERNAL_NOTIFICATIONS" as const;

    /**
     * If enabled, it will be possible to assign a cost pool to the work order
     */
    public static readonly WorkOrderCostPool: string = "WORK_ORDER_COST_POOL";

    /**
     * If enabled, will display a "debug" view of organization contract external ids in user management for contact people.
     */
    public static readonly ShowContactPersonExternalIds: string = "SHOW_CONTACT_PERSON_EXTERNAL_IDS" as const;

    /**
     * If enabled, construction sites  are integrated from Task to ERP.
     */
    public static readonly ConstructionSiteIntegration: string = "CONSTRUCTION_SITE_INTEGRATION" as const;

    /// <summary>
    /// If enabled, create order request will be sent upon work order invoicing.
    /// </summary>
    public static readonly SendCreateOrderRequest: string = "SEND_CREATE_ORDER_REQUEST" as const;

    /**
     * If enabled, work order's date/time fields will have 15 minute accuracy instead of one hour.
     */
    public static readonly WorkOrder15MinuteAccuracy: string = "WORK_ORDER_15_MINUTE_ACCURACY" as const;

    /**
     * If enabled, users can log in using Azure Active Directory single sign on.
     */
    public static readonly AzureAdSso: string = "AZURE_AD_SSO" as const;

    /**
     * If enabled, users can get a work report with comments.
     * Typically, this will be enabled together with
     * @see UserSalaryHoursComment
     */
    public static readonly WorkReportWithComments: string = "WORK_REPORT_WITH_COMMENTS" as const;

    /**
     * If enabled, sales person user can use the app.
     */
    public static readonly SalesPerson: string = "SALES_PERSON" as const;

    /**
     * If enabled an information bar will be displayed to the users.
     * Text is controlled in admin settings.
     */
    public static readonly InfoBar: string = "INFO_BAR" as const;

    /**
     * If enabled it's indicating that an update has been completed recently and an information
     * text should be displayed to the user if INFO_BAR is enabled also.
     * Text is controlled in admin settings.
     */
    public static readonly ApplicationUpdated: string = "APPLICATION_UPDATED" as const;

    /**
     * If enabled mounters can edit only their personal hours and not others.
     */
    public static readonly PersonalMounterHours: string = "PERSONAL_MOUNTER_HOURS" as const;

    /**
     * If enabled the user will be redirected to the copied work order when copying from the work order page.
     */
    public static readonly RedirectToCopiedWorkOrder: string = "REDIRECT_TO_COPIED_WORK_ORDER" as const;

    /**
     * If enabled external id number will be added to the name of the customer as a prefix.
     */
    public static readonly CustomerNumberAddToName: string = "CUSTOMER_NUMBER_ADD" as const;

     /**
     * If enabled when adding a new work order, the project number needs to be added as a search parameter to the dropdown.
     */
     public static readonly ProjectNumberAddToConstructionSiteName: string = "WORK_ORDER_NEW_NAME_PROJECT_NUMBER" as const;

    /**
    * If enabled change the construction site if the name is empty,
    * the project number will be copied to the name if the project number is not empty.
    */
     public static readonly CopyConstructionSiteProjectToConstructionSiteName: string = "CONSTRUCTION_SITE_PROJECT_NAME_COPY_TO_NAME" as const;

    /**
     * If enabled the region configuration will be displayed in the product catalog and used it ERP integration.
     */
    public static readonly ProductRegions: string = "PRODUCT_REGIONS" as const;

    /**
     * If enabled, it displays ExternalId override column for equipment added on desktop work order page.
     */
    public static readonly ExternalIdForWorkOrderEquipment = "EXTERNAL_ID_FOR_WORK_ORDER_EQUIPMENT" as const;

    /**
     * If enabled, If the Product cost is Zero, highlight the row so that the user can easily see from where the price is missing
     */
    public static readonly WorkOrderProductCostZero = "WORK_ORDER_PRODUCTS_COST_ZERO" as const;

    /**
     * If enabled, allows to pick the time in DatePicker in mobile.
     */
    public static readonly WorkOrderPickTimeInMobile = "WORK_ORDER_PICK_TIME_IN_MOBILE" as const;

    /**
     * If enabled, after clicking on the user name in calendar, the user will be redirected to the user management page.
     */
    public static readonly CalendarRedirectToUserManagement = "CALENDAR_REDIRECT_TO_USER_MANAGEMENT" as const;

    /**
     * If enabled, allows to apply custom organization contract settings
     */
    public static readonly OrganizationContractSettings = "ORGANIZATION_CONTRACT_SETTINGS" as const;

    /// <summary>
    /// If enabled, it will allow including rental items in create order request
    /// </summary>
    public static readonly SendRentalItems: string = "SEND_RENTAL_ITEMS" as const;

    /// <summary>
    /// Allow managers to change the status to invoiced or send the WO to the ERP.
    /// </summary>
    public static readonly WorkOrderAllowInvoiceManagers: string = "WORK_ORDER_INVOICE_MANAGERS" as const;

    /// <summary>
    /// If enabled, it will allow to create work orders with depot work type.
    /// </summary>
    public static readonly DepotWork: string = "DEPOT_WORK" as const;

    /// <summary>
    /// If enabled, the default normal hours will be set to zero.
    /// </summary>
    public static readonly ZeroNormalHoursByDefault: string = "ZERO_NORMA_HOURS_BY_DEFAULT" as const;

    /// <summary>
    /// If enabled, managers and mounters can approve work orders that have been declined by customer
    /// </summary>
    public static readonly WorkOrderApproveDeclined: string = "WORK_ORDER_APPROVE_DECLINED" as const;

    /**
     * If enabled, managers can mark attachments as requiring acknowledgment from mounters.
     */
    public static readonly AttachmentAcknowledgment: string = "ATTACHMENT_ACKNOWLEDGMENT" as const;

    /**
     * If enabled, priority news items force users to read and acknowledge them via a blocking modal.
     */
    public static readonly PriorityNews: string = "PRIORITY_NEWS" as const;

    /**
     * If enabled, user's work time will be sent to external system.
     */
    public static readonly SendWorkTime: string = "SEND_WORK_TIME" as const;

    /**
     * If enabled, allows setting default work order values for users in User Management.
     */
    public static readonly DefaultWorkOrderValues: string = "DEFAULT_WORK_ORDER_VALUES" as const;

    /**
     * If enabled, importing users from queueing system is available.
     */
    public static readonly UserImport: string = "USER_IMPORT" as const;

    /**
     * If enabled, sending field service work orders to external systems is available.
     */
    public static readonly SendFieldServiceWorkOrders: string = "SEND_FIELD_SERVICE_WORK_ORDERS" as const;

    /**
     * Activates Field Service work order type, logic, and UI behavior.
     */
    public static readonly FieldService: string = "FIELD_SERVICE" as const;

    /**
     * Enables the Absence WO contract type and Absence hours field (Sweden only).
     * Unleash resolves full key as TASK_ABSENCE_FIELDS_{country}.
     */
    public static readonly AbsenceFields: string = "ABSENCE_FIELDS" as const;

    /**
     * Enables absence management on the account page and related APIs.
     * Unleash resolves full key as TASK_ABSENCE_MANAGEMENT_{country}.
     */
    public static readonly AbsenceManagement: string = "ABSENCE_MANAGEMENT" as const;

    /**
     * Hides the Absence contract type from WO creation when full-day absences use absence management.
     * Unleash resolves full key as TASK_ABSENCE_HIDE_CONTRACT_TYPE_{country}.
     */
    public static readonly AbsenceHideContractType: string = "ABSENCE_HIDE_CONTRACT_TYPE" as const;

    /**
     * If enabled, managers receive on-demand notifications when assigned as task manager on work order create or edit.
     */
    public static readonly WorkOrderManagerAssignment: string = "WORK_ORDER_MANAGER_ASSIGNMENT" as const;

    /**
     * If enabled, managers and above can switch between desktop and mobile views.
     */
    public static readonly ViewModeSwitch: string = "VIEW_MODE_SWITCH" as const;

    /**
     * If enabled, PDF rendering uses the templates written for PDF service 2.0 (Playwright instead of IronPdf).
     * Backend only, there is no frontend behaviour attached to this flag.
     * Unleash resolves full key as TASK_PDF_SERVICE_2_{country}.
     */
    public static readonly PdfService2: string = "PDF_SERVICE_2" as const;
}
